import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { ChatbotThread } from '../../models/chatbot/types';
import { formatNarrowTimeAgo } from '../../lib/format-narrow-time-ago';
import ChatThreadOptionsMenu from './ChatThreadOptionsMenu';
import styles from './ChatHistoryItem.module.scss';

export interface ChatHistoryItemProps {
  thread: ChatbotThread;
  isSelected: boolean;
  onSelect: (threadId: string) => void;
  onRename: (threadId: string, newTitle: string) => void;
  onArchive: (threadId: string) => void;
}

const ChatHistoryItem: React.FC<ChatHistoryItemProps> = ({
  thread,
  isSelected,
  onSelect,
  onRename,
  onArchive,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(thread.title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = useCallback(() => {
    if (!isEditing) {
      onSelect(thread.id);
    }
  }, [onSelect, thread.id, isEditing]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isEditing) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(thread.id);
      }
    },
    [onSelect, thread.id, isEditing]
  );

  const handleRenameStart = useCallback(() => {
    setEditTitle(thread.title);
    setIsEditing(true);
  }, [thread.title]);

  const handleArchive = useCallback(() => {
    onArchive(thread.id);
  }, [onArchive, thread.id]);

  const handleEditSubmit = useCallback(() => {
    const trimmed = editTitle.trim();
    if (trimmed && trimmed !== thread.title) {
      onRename(thread.id, trimmed);
    }
    setIsEditing(false);
  }, [editTitle, thread.id, thread.title, onRename]);

  const handleEditCancel = useCallback(() => {
    setEditTitle(thread.title);
    setIsEditing(false);
  }, [thread.title]);

  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleEditSubmit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleEditCancel();
      }
    },
    [handleEditSubmit, handleEditCancel]
  );

  const handleEditChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditTitle(e.target.value);
    },
    []
  );

  const relativeTime = thread.lastMessageAt
    ? formatNarrowTimeAgo(thread.lastMessageAt)
    : '';
  const itemLabel =
    thread.source === 'search'
      ? t('assistant.historyItemSearchLabel', 'Search: {{title}}', {
          title: thread.title,
        })
      : t('assistant.historyItemChatLabel', 'Chat: {{title}}', {
          title: thread.title,
        });

  return (
    <div
      className={cx(styles.historyItem, { [styles.selected]: isSelected })}
      data-testid={`chat-history-item-${thread.id}`}
    >
      <div
        className={styles.itemContent}
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-label={itemLabel}
      >
        <div className={styles.titleRow}>
          {isEditing ? (
            <input
              ref={inputRef}
              className={styles.renameInput}
              type="text"
              value={editTitle}
              onChange={handleEditChange}
              onKeyDown={handleEditKeyDown}
              onBlur={handleEditSubmit}
              data-testid="rename-input"
              aria-label="Rename thread"
            />
          ) : (
            <>
              <span className={styles.title} data-testid="thread-title">
                {thread.title}
              </span>
              {relativeTime && (
                <span className={styles.timestamp}>{relativeTime}</span>
              )}
            </>
          )}
        </div>
        {!isEditing && thread.lastMessagePreview && (
          <div className={styles.preview}>
            <span className={styles.previewAccent} />
            <span className={styles.previewText}>
              {thread.lastMessagePreview}
            </span>
          </div>
        )}
      </div>
      <div className={styles.menuTrigger}>
        <ChatThreadOptionsMenu
          threadId={thread.id}
          onRename={handleRenameStart}
          onArchive={handleArchive}
        />
      </div>
    </div>
  );
};

export default ChatHistoryItem;
